import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { IMFPPStore } from '../../stores';
import { useSearchParams } from 'react-router-dom';
import { useRefetchOnVisibilityAndRoute } from '../tools/use-refetch-on-visibility-and-route';
import { FilterModel } from '@monorepo/controlled/src/models/filter.model';
import { NumberActions, RangeActions } from '@monorepo/tools/src/lib/utils/number';
import { StringActions } from '@monorepo/tools/src/lib/utils/string';
import { IInventoryRequestParams, InventoryApi } from '../../apis/inventory.api';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { InventoryStoreFiltersKeys } from '../../stores/inventory/inventory.store';
import { INVENTORY_DEFAULT_SORT } from '../../stores/inventory/inventory.constants';

export { INVENTORY_DEFAULT_SORT };

const STALE_THRESHOLD_MS = 5 * 60 * 1000;

function serializeFiltersToParams(filters: Map<number, FilterModel> | undefined): Partial<IInventoryRequestParams> {
	if (!filters || filters.size === 0) {
		return {};
	}
	const params: Partial<IInventoryRequestParams> = {};
	for (const [, filter] of filters) {
		const {
			prototype: { prop },
			value,
			action,
		} = filter;
		if (prop === 'inventorydomain' && typeof value === 'string') {
			// Prefix match only — backend filters with cpr.domain LIKE 'prefix%' (index-friendly).
			if (action && action !== StringActions.StartsWith) {
				continue;
			}
			const trimmed = value.trim();
			if (trimmed) {
				params.domain = trimmed;
			}
		} else if (prop === 'inventorygeo') {
			const vals = Array.isArray(value) ? value : [String(value)];
			params.geo = vals.map(String).join(',');
		} else if (prop === 'inventorydeepLink') {
			const vals = Array.isArray(value) ? value : [String(value)];
			params.deeplink = vals[0]?.toLowerCase();
		} else if (prop === 'inventoryclickCap' && typeof value === 'number') {
			const num = String(value);
			if (action === NumberActions.GreaterThan) {
				params.clickCapMin = String(value + 1);
			} else if (action === NumberActions.GreaterThanEquals) {
				params.clickCapMin = num;
			} else if (action === NumberActions.LessThan) {
				params.clickCapMax = String(value - 1);
			} else if (action === NumberActions.LessThanEquals) {
				params.clickCapMax = num;
			} else if (action === NumberActions.Equals) {
				params.clickCapMin = num;
				params.clickCapMax = num;
			}
		} else if (prop === 'inventorycommission' && typeof value === 'number') {
			if (action === RangeActions.GreaterThanRange) {
				params.commissionMin = String(value);
			} else if (action === RangeActions.LessThanRange) {
				params.commissionMax = String(value);
			}
		} else if (prop === 'inventoryvertical') {
			const vals = Array.isArray(value) ? value : [String(value)];
			params.vertical = vals.join(',');
		}
	}
	return params;
}

function buildRequestKey(accountId: string, page: number, pageSize: number, params: Partial<IInventoryRequestParams>): string {
	return JSON.stringify({ accountId, page, pageSize, ...params });
}

export function useInventoryList() {
	const { inventoryStore, publisherStore, settingsStore } = useStores<IMFPPStore>();
	const [searchParams, setSearchParams] = useSearchParams();
	const accountId = searchParams.get('accountId');
	const { currentRouteWithoutSpecialChars } = useRoute();

	const inventoryHttpStore = inventoryStore.getListStore();

	const tableStore = settingsStore.getPages().get(currentRouteWithoutSpecialChars)?.tableStore;
	const filterStore = settingsStore
		.getPages()
		.get(currentRouteWithoutSpecialChars)
		?.tableStore.getFiltersStore(currentRouteWithoutSpecialChars as InventoryStoreFiltersKeys);
	const currentFilters = filterStore?.getCurrentFilters();
	const filtersKey = JSON.stringify(Array.from((currentFilters || new Map()).values()));

	const sortingByKey = JSON.stringify(tableStore?.getSortingBy() ?? []);

	const page1InFlightRef = useRef(false);
	const page1RequestKeyRef = useRef<string | null>(null);
	const fetchPageRef = useRef<((page: number) => void) | null>(null);
	const skipSortDrivenFetchRef = useRef(true);
	const lastLoadedAccountRef = useRef<string | null>(null);

	const getCurrentFilterParams = useCallback((): Partial<IInventoryRequestParams> => {
		return serializeFiltersToParams(filterStore?.getCurrentFilters());
	}, [filterStore]);

	const getCurrentSortParams = useCallback((): Partial<IInventoryRequestParams> => {
		const first = (tableStore?.getSortingBy() ?? [])[0];
		if (!first) {
			return { sortBy: INVENTORY_DEFAULT_SORT[0].id, sortDir: INVENTORY_DEFAULT_SORT[0].desc ? 'desc' : 'asc' };
		}
		return { sortBy: first.id, sortDir: first.desc ? 'desc' : 'asc' };
	}, [tableStore]);

	const fetchPage = useCallback(
		(page: number) => {
			if (!accountId) {
				return;
			}

			const filterParams = getCurrentFilterParams();
			const sortParams = getCurrentSortParams();
			const requestKey = buildRequestKey(accountId, page, inventoryStore.pageSize, { ...filterParams, ...sortParams });

			if (page === 1 && page1InFlightRef.current && page1RequestKeyRef.current === requestKey) {
				return;
			}

			inventoryStore.setPage(page);
			const existingList = page > 1 ? inventoryHttpStore.getData()?.list ?? [] : [];
			inventoryHttpStore.abort();
			if (page === 1) {
				page1RequestKeyRef.current = requestKey;
				page1InFlightRef.current = true;
				inventoryHttpStore.reset();
			}

			inventoryHttpStore
				.fetch({
					accountId,
					page,
					pageSize: inventoryStore.pageSize,
					...filterParams,
					...sortParams,
				})
				.then(response => {
					if (!response) {
						return;
					}
					if (page === 1) {
						inventoryHttpStore.setData(response);
					} else {
						inventoryHttpStore.setData({
							...response,
							list: [...existingList, ...response.list],
						});
					}
					inventoryStore.setPaginationMeta(response.total, response.page, response.pageSize, response.hasMore);
					inventoryStore.setLastFetchedAt(Date.now());
				})
				.finally(() => {
					if (page === 1 && page1RequestKeyRef.current === requestKey) {
						page1InFlightRef.current = false;
					}
				});
		},
		[accountId, inventoryHttpStore, inventoryStore, getCurrentFilterParams, getCurrentSortParams]
	);

	fetchPageRef.current = fetchPage;

	const refetchFromPage1 = useCallback(() => {
		page1InFlightRef.current = false;
		page1RequestKeyRef.current = null;
		fetchPage(1);
	}, [fetchPage]);

	const silentRefetch = useCallback(() => {
		if (!accountId) {
			return;
		}
		if (Date.now() - inventoryStore.lastFetchedAt < STALE_THRESHOLD_MS) {
			return;
		}
		if (inventoryStore.page > 1) {
			inventoryStore.setLastFetchedAt(Date.now());
			return;
		}
		InventoryApi.list({
			accountId,
			page: 1,
			pageSize: inventoryStore.pageSize,
			...getCurrentFilterParams(),
			...getCurrentSortParams(),
		}).then(response => {
			if (!response) {
				return;
			}
			inventoryHttpStore.setData(response);
			inventoryStore.setPaginationMeta(response.total, 1, response.pageSize, response.hasMore);
			inventoryStore.setLastFetchedAt(Date.now());
		});
	}, [accountId, inventoryHttpStore, inventoryStore, getCurrentFilterParams, getCurrentSortParams]);

	useLayoutEffect(() => {
		if (accountId && tableStore) {
			tableStore.setPageIndex(0);
		}
	}, [accountId, tableStore]);

	useEffect(() => {
		if (!accountId) {
			lastLoadedAccountRef.current = null;
			skipSortDrivenFetchRef.current = true;
			page1InFlightRef.current = false;
			page1RequestKeyRef.current = null;
			inventoryHttpStore.reset();
			inventoryHttpStore.abort();
			inventoryHttpStore.setIsLoading(false);
			inventoryHttpStore.setData(null);
			inventoryHttpStore.setHttpError(null);
			inventoryStore.setPaginationMeta(0, 1, inventoryStore.pageSize, false);
			const firstAccountId = publisherStore.getListStore().getData()?.[0]?.id;
			if (firstAccountId) {
				setSearchParams({ accountId: `${firstAccountId}` });
			}
			return;
		}

		const accountChanged = lastLoadedAccountRef.current !== accountId;
		if (accountChanged) {
			lastLoadedAccountRef.current = accountId;
			skipSortDrivenFetchRef.current = true;
		}

		fetchPageRef.current?.(1);
	}, [accountId, filtersKey]);

	useEffect(() => {
		if (!accountId) {
			return;
		}
		if (skipSortDrivenFetchRef.current) {
			skipSortDrivenFetchRef.current = false;
			return;
		}
		fetchPageRef.current?.(1);
	}, [accountId, sortingByKey]);

	const touchTimestamp = useCallback(() => {
		if (inventoryStore.lastFetchedAt > 0) {
			inventoryStore.setLastFetchedAt(Date.now());
		}
	}, [inventoryStore]);

	useRefetchOnVisibilityAndRoute(touchTimestamp, Boolean(accountId));

	useEffect(() => {
		if (!accountId) {
			return;
		}
		const id = setInterval(silentRefetch, STALE_THRESHOLD_MS);
		return () => clearInterval(id);
	}, [accountId, silentRefetch]);

	return {
		error: inventoryHttpStore.getHttpError(),
		isLoading: inventoryHttpStore.getIsLoading(),
		isSuccess: inventoryHttpStore.getIsSuccess(),
		data: inventoryHttpStore.getData()?.list ?? [],
		total: inventoryStore.total,
		page: inventoryStore.page,
		pageSize: inventoryStore.pageSize,
		hasMore: inventoryStore.hasMore,
		gotoPage: fetchPage,
		refetchFromPage1,
	};
}
